import { Fragment } from "react";
import { t } from "ttag";

import { FIELD_TYPE } from "@/features/forms/models";
import type {
  FunnelIntakeAnswers,
  FunnelIntakeOption,
  FunnelIntakeQuestion as Question,
} from "../models";
import { SKIPPED_ANSWER } from "../models";
import type { FunnelIntakeQuestionIndex } from "../utils/intake-screens";
import {
  blockingMessages,
  isQuestionVisible,
  toggleOption,
} from "../utils/intake-screens";
import {
  FunnelCheckboxGroup,
  FunnelCheckboxRow,
  FunnelChoiceCard,
  FunnelFieldLabel,
  FunnelNumberInput,
  FunnelSelect,
  FunnelTextarea,
} from "./funnel-fields";
import { FUNNEL_ICON } from "./funnel-icons";

interface FunnelIntakeQuestionProps {
  question: Question;
  answers: FunnelIntakeAnswers;
  questionsByKey: FunnelIntakeQuestionIndex;
  onChange: (key: string, value: string | string[]) => void;
  /**
   * `row` renders the question in the plain style of a checkbox list, for a
   * follow-up nested in one.
   */
  variant?: "card" | "row";
}

const iconOf = (option: FunnelIntakeOption) =>
  option.icon ? FUNNEL_ICON[option.icon] : undefined;

export const FunnelIntakeQuestion = ({
  question,
  answers,
  questionsByKey,
  onChange,
  variant = "card",
}: FunnelIntakeQuestionProps) => {
  const answer = answers[question.key];
  const selected = Array.isArray(answer) ? answer : answer ? [answer] : [];
  const setAnswer = (value: string | string[]) => onChange(question.key, value);

  const shownFollowUpKeys = new Set<string>();
  const followUpsFor = (option: FunnelIntakeOption) => {
    if (!selected.includes(option.value)) {
      return [];
    }

    return option.followUps.filter((followUp) => {
      if (
        shownFollowUpKeys.has(followUp.key) ||
        !isQuestionVisible(followUp, answers, questionsByKey)
      ) {
        return false;
      }
      shownFollowUpKeys.add(followUp.key);

      return true;
    });
  };

  const renderFollowUps = (
    followUps: Question[],
    followUpVariant: "card" | "row" = variant,
  ) =>
    followUps.length > 0 && (
      <div
        className={
          followUpVariant === "row"
            ? "mt-2 mb-2 ml-9 flex flex-col gap-6"
            : "mt-8 flex flex-col gap-8"
        }
      >
        {followUps.map((followUp) => (
          <FunnelIntakeQuestion
            key={followUp.key}
            question={followUp}
            answers={answers}
            questionsByKey={questionsByKey}
            onChange={onChange}
            variant={followUpVariant}
          />
        ))}
      </div>
    );

  const label = question.label && (
    <FunnelFieldLabel>{question.label}</FunnelFieldLabel>
  );

  const messages = blockingMessages(question, answers).map((message) => (
    <p
      key={message}
      role="alert"
      className="mt-2 text-sm font-medium text-red-700"
    >
      {message}
    </p>
  ));

  switch (question.input) {
    case FIELD_TYPE.RADIO: {
      const followUps = question.options.flatMap(followUpsFor);

      if (variant === "row") {
        return (
          <div>
            {label}
            <div className="flex flex-col">
              {question.options.map((option) => (
                <FunnelCheckboxRow
                  key={option.value}
                  type="radio"
                  label={option.label}
                  checked={selected.includes(option.value)}
                  onChange={() => setAnswer(option.value)}
                />
              ))}
            </div>
            {messages}
            {renderFollowUps(followUps)}
          </div>
        );
      }

      return (
        <div>
          {label}
          <div
            className={`grid gap-4 ${question.options.length === 2 ? "grid-cols-2" : ""}`}
          >
            {question.options.map((option) => (
              <FunnelChoiceCard
                key={option.value}
                type="radio"
                icon={iconOf(option)}
                label={option.label}
                selected={selected.includes(option.value)}
                onSelect={() => setAnswer(option.value)}
              />
            ))}
          </div>
          {messages}
          {renderFollowUps(followUps)}
        </div>
      );
    }

    case FIELD_TYPE.CHECKBOX: {
      const exclusiveValues = question.options
        .filter((option) => option.exclusive)
        .map((option) => option.value);

      if (
        variant === "card" &&
        question.options.some((option) => option.icon)
      ) {
        return (
          <div>
            {label}
            <div className="grid gap-4">
              {question.options.map((option) => (
                <Fragment key={option.value}>
                  <FunnelChoiceCard
                    type="checkbox"
                    icon={iconOf(option)}
                    label={option.label}
                    selected={selected.includes(option.value)}
                    onSelect={() =>
                      setAnswer(
                        toggleOption(selected, option.value, exclusiveValues),
                      )
                    }
                  />
                  {renderFollowUps(followUpsFor(option))}
                </Fragment>
              ))}
            </div>
            {messages}
          </div>
        );
      }

      return (
        <div>
          {label}
          <FunnelCheckboxGroup
            options={question.options.map((option) => ({
              value: option.value,
              label: option.label,
              emphasized: option.emphasized,
              children: renderFollowUps(followUpsFor(option), "row"),
            }))}
            value={selected}
            onChange={setAnswer}
            exclusiveValues={exclusiveValues}
          />
          {messages}
        </div>
      );
    }

    case FIELD_TYPE.TEXT:
    case FIELD_TYPE.TEXTAREA:
      return (
        <div>
          <FunnelTextarea
            label={question.label}
            placeholder={question.placeholder ?? undefined}
            value={
              typeof answer === "string" && answer !== SKIPPED_ANSWER
                ? answer
                : ""
            }
            onChange={setAnswer}
          />
          {question.skippable && (
            <FunnelCheckboxRow
              checked={answer === SKIPPED_ANSWER}
              onChange={(checked) => setAnswer(checked ? SKIPPED_ANSWER : "")}
              label={t`Skip, I'll discuss it with my doctor`}
            />
          )}
        </div>
      );

    case FIELD_TYPE.NUMBER:
      return (
        <FunnelNumberInput
          label={question.label}
          placeholder={question.placeholder ?? undefined}
          value={typeof answer === "string" ? answer : ""}
          onChange={setAnswer}
        />
      );

    case FIELD_TYPE.DROPDOWN:
      return (
        <div>
          {label}
          <FunnelSelect
            value={typeof answer === "string" ? answer : undefined}
            onChange={setAnswer}
            options={question.options}
            placeholder={question.placeholder ?? undefined}
          />
          {messages}
          {renderFollowUps(question.options.flatMap(followUpsFor))}
        </div>
      );

    case FIELD_TYPE.HORIZONTAL_RADIO:
    case FIELD_TYPE.READ_ONLY:
    case FIELD_TYPE.READ_ONLY_LINK:
    case FIELD_TYPE.FILE:
    case FIELD_TYPE.HIDDEN:
      return null;

    default: {
      const unsupported: never = question.input;

      return unsupported;
    }
  }
};
